import googlePhone from 'google-libphonenumber'
import { VALID_ENG_COUNTRIES, VALID_NA_COUNTRIES } from 'src/constants/valid-countries'

// getInstance() is a singleton, so this is the same object bootstrap.js exposes
// as window.phoneUtil.
const phoneUtil = googlePhone.PhoneNumberUtil.getInstance()

/**
 * Resolve a phone number to a North American region, or false.
 */
export function getLocaleIfPhoneNumberIsFromNorthAmerica (phoneNumber) {
  if (!phoneNumber) {
    return false
  }

  try {
    for (const validCountry of VALID_NA_COUNTRIES) {
      const number = phoneUtil.parseAndKeepRawInput(phoneNumber, validCountry)
      let isPossible = phoneUtil.isPossibleNumber(number)

      if (isPossible && phoneUtil.isValidNumberForRegion(number, validCountry)) {
        return validCountry
      }
    }

    // Fallback: accept numbers that are "possible" for US/CA even when strict
    // region validation fails — new area codes (e.g. 472, introduced Oct 2022)
    // can lag behind the library's metadata. The country-code guard prevents
    // explicit-international numbers (e.g. +5511...) from being misclassified
    // as US — libphonenumber honors the + prefix over the region hint, so
    // isPossibleNumber alone would let them through.
    for (const validCountry of ['US', 'CA']) {
      const number = phoneUtil.parseAndKeepRawInput(phoneNumber, validCountry)
      if (phoneUtil.isPossibleNumber(number) && number.getCountryCode() === 1) {
        return validCountry
      }
    }

    return false
  } catch (err) {
    return false
  }
}

/**
 * Resolve a phone number to GB, AU or IE, or false.
 */
export function getLocaleIfPhoneNumberIsFromGreatBritainOrAustralia (phoneNumber) {
  if (!phoneNumber) {
    return false
  }

  try {
    for (let validCountry of VALID_ENG_COUNTRIES) {
      let number = phoneUtil.parseAndKeepRawInput(phoneNumber, validCountry)
      let isPossible = phoneUtil.isPossibleNumber(number)

      if (isPossible && phoneUtil.isValidNumberForRegion(number, validCountry)) {
        return validCountry
      }
    }

    return false
  } catch (err) {
    return false
  }
}

/**
 * Guess the region a dialed string belongs to, or false when none fits.
 */
export function guessLocale (phoneNumber) {
  if (!phoneNumber) {
    return false
  }

  // Use substring() and indexOf() functions to remove
  // portion of string after certain character (w => wait)
  // example, the extension wwww2wwwwww5wwwwww9 waits 2 seconds
  // before sending the digit 2, followed by a three second wait
  // before sending th 5, and finally another three second wait
  // before sending a 9. These are numbers behind IVR/extension
  let pos = phoneNumber.indexOf('w')

  if (pos !== -1) {
    phoneNumber = phoneNumber.substring(0, pos).trim()
  }

  try {
    // A single leading 0 is a national trunk prefix and NANP has none, so the checks below
    // would only mis-resolve it (0417178685 => +10417178685). 00 stands in for a +, not a trunk.
    // Read it off the digits, not the raw string: fixPhone strips whitespace, # and . but
    // leaves brackets, and an AU area code is conventionally written (02) 1234 5678, which
    // would otherwise hide the 0 and put the number back on the NANP path.
    const digits = phoneNumber.replace(/\D/g, '')
    const hasTrunkPrefix = digits.startsWith('0') && !digits.startsWith('00')

    if (!hasTrunkPrefix) {
      // handle US and CA as an special case
      let northAmericaLocale = getLocaleIfPhoneNumberIsFromNorthAmerica(phoneNumber)

      if (northAmericaLocale) {
        return northAmericaLocale
      }

      // will add + to phone number and check again
      if (!phoneNumber.includes('+')) {
        northAmericaLocale = getLocaleIfPhoneNumberIsFromNorthAmerica('+' + phoneNumber)

        if (northAmericaLocale) {
          return northAmericaLocale
        }
      }

      // will add +1 to phone number and check again
      if (!phoneNumber.includes('+')) {
        northAmericaLocale = getLocaleIfPhoneNumberIsFromNorthAmerica('+1' + phoneNumber)

        if (northAmericaLocale) {
          return northAmericaLocale
        }
      }
    }

    // handle GB & AU as a special case
    let englishLocale = getLocaleIfPhoneNumberIsFromGreatBritainOrAustralia(phoneNumber)

    if (englishLocale) {
      return englishLocale
    }

    // if we reached here then it's definitely not a US or CA number according to google-libphonenumber
    // let's check for international locales

    if (!phoneNumber.includes('+')) {
      phoneNumber = '+' + phoneNumber
    }

    let number = phoneUtil.parse(phoneNumber)
    let locale = phoneUtil.getRegionCodeForNumber(number)
    let isValid = phoneUtil.isValidNumber(number)

    if (!locale) {
      number = phoneUtil.parse(phoneNumber, 'US')
      locale = phoneUtil.getRegionCodeForNumber(number)
      isValid = phoneUtil.isValidNumber(number)

      return isValid
    }

    return locale
  } catch (err) {
    return false
  }
}
